import { useState, useEffect } from "react";
import { Plus, Edit, Trash2, Star, Users as UsersIcon } from "lucide-react";
import api from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";

export default function AdminInstructors() {
  const [instructors, setInstructors] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", bio: "", image_url: "", specialization: "" });

  useEffect(() => { fetchData(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const { data } = await api.get("/instructors");
      setInstructors(data);
    } catch (err) { console.error(err); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/instructors/${editing}`, form);
      } else {
        await api.post("/instructors", form);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ name: "", email: "", phone: "", bio: "", image_url: "", specialization: "" });
      fetchData();
    } catch (err) { console.error(err); }
  };

  const handleEdit = (inst) => {
    setForm({ name: inst.name, email: inst.email || "", phone: inst.phone || "", bio: inst.bio || "", image_url: inst.image_url || "", specialization: inst.specialization || "" });
    setEditing(inst.id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this instructor?")) {
      await api.delete(`/instructors/${id}`);
      fetchData();
    }
  };

  const inputClass = "w-full admin-input border-b-2 px-4 py-3 text-sm focus:outline-none focus:border-[#0284C7] transition-none";

  return (
    <AdminLayout>
      <div data-testid="admin-instructors-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-black admin-text" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Instructors
            </h1>
            <p className="text-sm admin-text-secondary mt-0.5">{instructors.length} instructors</p>
          </div>
          <button
            data-testid="add-instructor-btn"
            onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ name: "", email: "", phone: "", bio: "", image_url: "", specialization: "" }); }}
            className="bg-[#0284C7] text-white font-bold py-2.5 px-4 text-xs flex items-center gap-1.5 transition-none"
          >
            <Plus size={14} /> Add Instructor
          </button>
        </div>

        {showForm && (
          <form onSubmit={handleSubmit} className="admin-card border p-5 mb-6 space-y-3" data-testid="instructor-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Name</label>
                <input data-testid="inst-name-input" placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputClass} required />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Email</label>
                <input type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Phone</label>
                <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputClass} />
              </div>
              <div>
                <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Specialization</label>
                <input placeholder="e.g. English Language" value={form.specialization} onChange={(e) => setForm({ ...form, specialization: e.target.value })} className={inputClass} />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Image URL</label>
              <input placeholder="Profile photo URL" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} className={inputClass} />
            </div>
            <div>
              <label className="text-[10px] font-bold admin-text-secondary uppercase tracking-wider">Bio</label>
              <textarea placeholder="Brief bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })} className={`${inputClass} h-20`} />
            </div>
            <div className="flex gap-2">
              <button data-testid="inst-submit-btn" type="submit" className="bg-[#0284C7] text-white font-bold py-2.5 px-6 text-xs transition-none">
                {editing ? "Update" : "Add"} Instructor
              </button>
              <button type="button" onClick={() => { setShowForm(false); setEditing(null); }} className="admin-card border font-bold py-2.5 px-6 text-xs transition-none admin-text">
                Cancel
              </button>
            </div>
          </form>
        )}

        {/* Instructor Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="instructors-grid">
          {instructors.map((inst) => (
            <div key={inst.id} className="admin-card border p-5">
              <div className="flex items-start gap-4 mb-4">
                <div className="w-14 h-14 bg-slate-200 flex-shrink-0 overflow-hidden">
                  {inst.image_url ? (
                    <img src={inst.image_url} alt={inst.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-[#0284C7] text-white flex items-center justify-center text-lg font-bold">
                      {inst.name?.charAt(0)}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-bold admin-text">{inst.name}</h3>
                  <p className="text-[10px] text-[#0284C7] font-medium">{inst.specialization || "General"}</p>
                  <p className="text-xs admin-text-secondary">{inst.email}</p>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => handleEdit(inst)} className="p-1 admin-text-secondary"><Edit size={14} /></button>
                  <button onClick={() => handleDelete(inst.id)} className="p-1 text-red-400"><Trash2 size={14} /></button>
                </div>
              </div>
              <p className="text-xs admin-text-secondary leading-relaxed mb-3 line-clamp-2">{inst.bio || "No bio provided"}</p>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1 admin-text-secondary">
                  <Star size={12} className="text-amber-400" /> {inst.rating || 0}
                </span>
                <span className="flex items-center gap-1 admin-text-secondary">
                  <UsersIcon size={12} /> {inst.students || 0} students
                </span>
                <span className="admin-text-secondary">{inst.courses_count || 0} courses</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
